export type BillingStatus = 1 | 2 | 3 | 4 | 5;
export type BillingType = 1 | 2 | 3;

export const BillingStatusName: Record<BillingStatus, string> = {
  1: "Pending",
  2: "Billed",
  3: "Received",
  4: "Cancelled",
  5: "Service Order",
};

export const BillingTypeName: Record<BillingType, string> = {
  1: "Fixed Price",
  2: "Hourly Rate",
  3: "Recurring",
};

export interface ClientTimesheetDetail {
  timeSheetDetailId: number;
  timeSheetId: number;
  clientId: number;
  clientName: string;
  employeeId: number;
  employeeName: string;
  projectId: number | null;
  projectName: string;
  dayRef: number;
  monthRef: number;
  yearRef: number;
  hoursWorked: number;
  dsActivity: string | null;
  linkedBillingId: number | null;
  projectHourlyRate: number | null;
  projectBillingType: BillingType | null;
}

export interface BillingEquipment {
  equipmentId: number;
  model: string;
  serieNumber: string;
  valueSnapshot: number;
}

export interface ClientEquipmentOption {
  equipmentId: number;
  model: string;
  serieNumber: string;
  monthlyValue: number;
  status: string;
  alreadyBilledThisMonth: boolean;
  linkedBillingId: number | null;
}

export interface Billing {
  billingId: number;
  clientId: number;
  clientName: string;
  projectId: number | null;
  projectName: string;
  monthRef: number;
  yearRef: number;
  amount: number;
  billingType: BillingType;
  billingTypeName: string;
  status: BillingStatus;
  statusName: string;
  phase: number; // 1 Pendente de Faturamento · 2 Faturado/NF · 3 NF emitida/receb · 4 Recebido · 0 Cancelado
  phaseName: string;
  invoiceNumber: string | null;
  dtEmitted: string | null;
  dtCharged: string | null;
  dtCreated: string;
  dtCancelled: string | null;
  cancelReason: string | null;
  /** Etapa no SIIMP (0 = não enviada · 2 NFS-e processando · 6 fatura emitida · 7 liquidada). */
  siimpEtapa: number;
  siimpEtapaNome: string;
  siimpErro: string | null;
  siimpFaturaNumero: string | null;
  dtVencimento: string | null;
  timeSheetDetails: ClientTimesheetDetail[];
  equipments: BillingEquipment[];
}

/** GET /Financial/Siimp/Preview/{billingId} — o que o modal de emissão mostra antes de confirmar. */
export interface SiimpPreview {
  habilitado: boolean;
  discriminacao: string;
  vencimento: string; // yyyy-MM-dd
  nfseServicoId: number;
  naturezaFiscal: number;
}

/** Resposta de EmitInvoice e de Siimp/Reprocessar. */
export interface EmitInvoiceResult {
  billingId: number;
  invoiceNumber: string;
  dtEmitted: string;
  siimpEtapa: number;
  siimpErro: string | null;
  siimpFaturaNumero: string | null;
}

/** Mensagem do resultado de uma emissão/reprocessamento pelo SIIMP. */
export const siimpResultadoMensagem = (r: EmitInvoiceResult): { ok: boolean; texto: string } => {
  if (r.siimpErro) return { ok: false, texto: r.siimpErro };
  if (r.siimpEtapa >= 6)
    return { ok: true, texto: `NFS-e nº ${r.invoiceNumber} emitida e fatura${r.siimpFaturaNumero ? ` nº ${r.siimpFaturaNumero}` : ""} gerada no SIIMP.` };
  if (r.invoiceNumber) return { ok: true, texto: `NFS-e nº ${r.invoiceNumber} emitida; a fatura está sendo gerada no SIIMP.` };
  return { ok: true, texto: "NFS-e enviada ao SIIMP. A prefeitura ainda está processando; o NINA atualiza sozinho." };
};

export interface CreateServiceOrderPayload {
  clientId: number;
  billingType: BillingType;
  monthRef: number;
  yearRef: number;
  amount: number;
  timeSheetDetailIds: number[];
  equipmentIds: number[];
}

export interface UpdateServiceOrderPayload {
  amount: number;
  timeSheetDetailIds: number[];
  equipmentIds: number[];
}

export interface EmployeeHours {
  employeeId: number;
  employeeName: string;
  clientId: number;
  clientName: string;
  projectId: number;
  projectName: string;
  monthRef: number;
  yearRef: number;
  totalHours: number;
}

export interface GenerateBillingsPayload {
  monthRef: number;
  yearRef: number;
}

export interface GenerateHourlyBillingPayload {
  projectId: number;
  clientId: number;
  amount: number;
  monthRef: number;
  yearRef: number;
}

export interface GenerateFixedPriceBillingPayload {
  projectId: number;
  clientId: number;
  amount: number;
  monthRef: number;
  yearRef: number;
}

export interface FinancialProject {
  id: number;
  projectName: string;
  billingType: BillingType;
  amount: number | null;
}

export interface ClientProjectItem {
  id: number;
  clientId: number;
  nmCorporate: string;
  projectId: number;
}

export interface FixedPriceBillingSummary {
  projectId: number;
  contractedAmount: number;
  totalBilled: number;
  remaining: number;
}

// ── Visão financeira por cliente/projeto ──────────────────────────────────────

export interface FixedPriceBurnDown {
  contractedHours: number | null;
  consumedHoursTotal: number;
  contractedAmount: number | null;
  percentConsumed: number | null;
  projectedEndDate: string | null;
}

export interface ProjectOverview {
  projectId: number;
  projectName: string;
  billingType: BillingType | null;
  active: boolean;
  hoursThisMonth: number;
  forecast: number;
  real: number;
  isBilled: boolean;
  /** Valor fixo: total já recebido (pago) do projeto. */
  paidAmount: number;
  /** Projeto de valor fixo totalmente pago (recebido ≥ contrato). True para os demais tipos. */
  isFullyPaid: boolean;
  fixedPrice: FixedPriceBurnDown | null;
}

export interface ClientBillingRef {
  billingId: number;
  status: number;
  phase: number;
  phaseName: string;
  amount: number;
  invoiceNumber: string | null;
}

export type BillingStage = "pending" | "to_invoice" | "received" | "none";

export interface ClientOverview {
  clientId: number;
  clientName: string;
  active: boolean;
  totalHours: number;
  currentForecast: number;
  currentReal: number;
  previousValue: number;
  previousIsReal: boolean;
  isBilledCurrent: boolean;
  needsAttention: boolean;
  /** Tem projeto de valor fixo ainda não totalmente recebido (não pago). */
  hasUnpaidFixedPrice: boolean;
  /** Estágio do faturamento do cliente no mês. */
  billingStage: BillingStage;
  /** Cobranças/OS do cliente no mês (com o ServiceOrder ID). */
  billings: ClientBillingRef[];
  projects: ProjectOverview[];
}

export interface FinancialOverview {
  month: number;
  year: number;
  totalForecast: number;
  totalReal: number;
  totalHours: number;
  previousMonthValue: number;
  previousMonthIsReal: boolean;
  clientsNeedingAttention: number;
  clients: ClientOverview[];
}

export interface WeeklyReportResult {
  weekStart: string;
  weekEnd: string;
  clientEmailsSent: number;
  employeeEmailsSent: number;
}

export interface WeeklyReportLogItem {
  id: number;
  recipientType: string; // "Client" | "Employee"
  recipientId: number;
  recipientName: string;
  email: string | null;
  periodStart: string;
  periodEnd: string;
  totalHours: number;
  success: boolean;
  errorMessage: string | null;
  sentAt: string;
}

// Extrato bancário (change baixa-extrato) — espelha os ViewModels de ExtratoDtos.cs.
export interface ImportacaoExtrato {
  lidos: number;
  creditos: number;
  novos: number;
  duplicados: number;
  conciliadosAutomaticamente: number;
  naoIdentificados: number;
}

export interface SugestaoCobranca {
  billingId: number;
  clientName: string;
  projectName: string | null;
  monthRef: number;
  yearRef: number;
  amount: number;
  invoiceNumber: string | null;
  motivo: string;
}

export interface LancamentoNaoIdentificado {
  id: number;
  dtLancamento: string;
  valor: number;
  descricao: string;
  documentoPagador: string | null;
  sugestoes: SugestaoCobranca[];
}

export interface CobrancaAberta {
  billingId: number;
  clientName: string;
  projectName: string | null;
  monthRef: number;
  yearRef: number;
  amount: number;
  invoiceNumber: string | null;
}
